import React, {useEffect, useRef, useState} from "react";
import {Modal, Spin, Tree} from "antd";
import {request} from "../../util/request";
import {GLOBAL_RULES_PROBLEM_API} from "../../config/api";
import {ObjTest, treeUtil} from "jzfz-web-utils";
import {getExpandedKeys,} from '../../util/common';
import {File3Icon} from "../icon/Icon";
import CustomEmpty from "../customEmpty/CustomEmpty";
import {FormInstance} from "antd/lib/form/hooks/useForm";
import {ModuleEnum} from "../../config/constant";


const { getListFromTree } = treeUtil;


interface RulesProbleTreeProps {
    moduleType?: ModuleEnum;    // 模块类型
    form?: FormInstance;
    defaultCheckedKeys?: string[];  // 默认选中key
    visible: boolean;
    onSubmit: (values: any[]) => void;
    onCancel: () => void;
}


/**
 * 规定问题选择弹窗
 * @param props
 * @constructor
 */
const RulesProblemModal = (props: RulesProbleTreeProps) => {
    const { moduleType, form, defaultCheckedKeys, visible, onSubmit, onCancel } = props;
    const [treeData, setTreeData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    // 线性目录树缓存
    let treeDataCache = useRef<any[]>([]);


    useEffect(() => {
        if (visible) {
            setCheckedKeys([]);
            setExpandedKeys([]);
            getTreeData();
        }
    }, [visible])


    /**
     * 获取规定问题树
     */
    const getTreeData = () => {
        setLoading(true);
        request('/regulation/generalCatalog/selectCatalogRegulation', {}, {baseURL: GLOBAL_RULES_PROBLEM_API}).then((res: any) => {
            let data = res.data || [];
            setTreeData(loop(data));
            treeDataCache.current = getListFromTree(data);
            // 设置选中结点key、展开结点key
            let keys = treeDataCache.current.filter((v: any) => defaultCheckedKeys.includes(v.pkid)).map((v: any) => v.pkid);
            let checkedKeys = [...keys];
            let expandedKeys = [];
            getExpandedKeys(data, expandedKeys, checkedKeys, 'pkid');
            setCheckedKeys(checkedKeys);
            setExpandedKeys(expandedKeys);
        }).finally(() => {
            setLoading(false);
        });
    }


    /**
     * 提交
     */
    const submit = () => {
        // 展开树结点
        const target = treeDataCache.current.filter((v: any) => checkedKeys.includes(v.pkid) && v.regulationPkid);
        if (!target) {
            return;
        }
        // 根据规定问题pkid集合查询相关信息
        let pkids = target.map((v: any) => v.regulationPkid);

        setLoading(true);
        request('/api/regulation/companyRegulationApi/selectRegulationsByIds',
            {regulationPkids: pkids},
            {baseURL: GLOBAL_RULES_PROBLEM_API,
        }).then((res: any) => {
            let values = res.data.map((v: any) => {
                return {regulationPkid: v.regulationPkid, regulationPath: v.regulationPath, regulationSimpleContent: v.regulationSimpleContent};
            });
            onSubmit(values);
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 勾选结点
     * @param info
     */
    const checkHandle = (info: any, e: any) => {
        const { checked } = info;
        const { checked: flag, node: {children = []} } = e;
        let newCheckedKeys = [...checked];
        let childrenKeys = getListFromTree(children).map((v: any) => v.key);
        if (flag) {
            newCheckedKeys = Array.from(new Set([...newCheckedKeys, ...childrenKeys]));
        }else {
            newCheckedKeys = newCheckedKeys.filter((v: any) => !childrenKeys.includes(v));
        }
        setCheckedKeys(newCheckedKeys);
    }


    /**
     * 展开树结点
     */
    const expandHandle = (expandedKeys: any[]) => {
        setExpandedKeys(expandedKeys);
    }


    /**
     * 获取checkbox禁用状态
     * @param regulationPkid
     * @param isContent 当前节点是内容节点
     */
    const getDisabled = (regulationPkid: string, isContent: boolean): boolean => {
        // 不能添加该问题本身
        if (moduleType === ModuleEnum.rules && form) {
            const { regulationPkid: pkid } = form.getFieldsValue();
            return !isContent || regulationPkid === pkid;
        }
        return !isContent;
    }


    const loop = (list: any[]): any[] => list.map((v: any) => {
        const { pkid, regulationPkid, content, children } = v;
        const title = regulationPkid ? content : v.catalogName;
        let isContent = pkid && regulationPkid; // 是否是内容节点
        return {
            title,
            key: pkid,
            icon: isContent ? <File3Icon style={{color: '#1887F1'}} /> : null,
            disableCheckbox: getDisabled(regulationPkid, isContent),
            children: ObjTest.isNotEmptyArray(children) ? loop(children) : [],
        }
    })


    return (
        <Modal
            forceRender={true}
            maskClosable={false}
            width={600}
            title="关联规定问题"
            closable={false}
            visible={visible}
            confirmLoading={loading}
            onOk={submit}
            onCancel={onCancel}
            okButtonProps={{className: 'button-red'}}
        >
            <div style={{maxHeight: 600, overflow: "auto"}}>
                <Spin spinning={loading}>
                    {
                        ObjTest.isNotEmptyArray(treeData) ? (
                            <Tree
                                height={600}
                                showIcon
                                checkable
                                selectable={false}
                                checkStrictly={true}
                                treeData={treeData}
                                checkedKeys={checkedKeys}
                                expandedKeys={expandedKeys}
                                onCheck={checkHandle}
                                onExpand={expandHandle}
                            />
                        ) : <CustomEmpty loading={loading} />
                    }
                </Spin>
            </div>
        </Modal>
    )
}


export default RulesProblemModal;
